import { MenuItem } from "@episerver/platform-navigation";
import { LayoutData } from "../components/layout";

const workTypeSortOrder = [
    "Foundation",
    "Components",
    "Patterns",
    "Products",
    "Develop",
    "Guidelines",
    "Internal UX",
    "Developers"
];

const workSubTypeSortOrder = [
    "Introduction",
    "Colors",
    "Layout",
    "Shape",
    "Iconography",
    "UI Framework"
];

const workItemSortOrder = [
    "Deprecated",
    "Getting started",
    "Introduction",
    "Understanding layout"
];

const navigationSort: any = (a: string, b: string, sortOrder: string[]) => {
    const indexOfA = sortOrder.indexOf(a);
    const indexOfB = sortOrder.indexOf(b);

    // Sort in specified order, unspecified items go at the end in alphabetical order
    if (indexOfA < 0 && indexOfB < 0) {
        return a.localeCompare(b);
    } else if (indexOfA < 0) {
        return 1;
    } else if (indexOfB < 0) {
        return -1;
    }

    return indexOfA - indexOfB;
};

export const mapMenuItems = (data: LayoutData): MenuItem[] => {
    const uniqueTopLevelItems = [
        ...new Set(
            data.allMdx.nodes
                .map(node => node.fields.topLevelDir.title)
        )
    ].sort((a, b) => navigationSort(a, b, workTypeSortOrder));

    const menuItems = uniqueTopLevelItems.map(item => {
        const topLevelItemUrl = data.allMdx.nodes.find(
            node => node.fields.topLevelDir.title === item
        ).fields.topLevelDir.url;
        const subMenuItems = data.allMdx.nodes.filter(
            subNode =>
                subNode.fields.topLevelDir.title === item &&
                subNode.fields.subDir.title
        );

        // If there are no sub-directories, we should skip right to work items
        // Work items need to be added as their own property, since children will
        // be added to the second level by the Navigation component
        if (subMenuItems.length < 1) {
            return {
                name: item,
                url: topLevelItemUrl,
                children: [],
                workItems: data.allMdx.nodes.filter(
                    subNode =>
                        subNode.fields.topLevelDir.title === item
                ).map(menuItem => ({
                    name: menuItem.frontmatter.title,
                    url: menuItem.fields.slug,
                    children: []
                })).sort((a, b) => navigationSort(a.name, b.name, workItemSortOrder))
            };
        }
        const uniqueSubMenuItems = [
            ...new Set(subMenuItems.map(subItem => subItem.fields.subDir.title))
        ].sort((a, b) => navigationSort(a, b, workSubTypeSortOrder));
        const menuItemChildren = uniqueSubMenuItems.map(subItem => {
            const subMenuChildren = subMenuItems
                .filter(menuItem => menuItem.fields.subDir.title === subItem)
                .map(menuItem => ({
                    name: menuItem.frontmatter.title,
                    url: menuItem.fields.slug,
                    children: []
                }))
                .sort((a, b) => navigationSort(a.name, b.name, workItemSortOrder));

            return {
                name: subItem,
                // the URL for a sub item will be the first URL in the folder after being sorted
                url: subMenuChildren[0].url,
                children: subMenuChildren
            };
        });
        return {
            name: item,
            url: topLevelItemUrl,
            children: menuItemChildren
        };
    });

    return menuItems;
};

const trimTrailingSlash = (url: string): string => {
    return url.endsWith("/") ? url.slice(0, -1) : url;
};

const pathMatchUrl = (path: string, item: { url: string }): boolean => {
    return path
        .toLowerCase()
        .includes(trimTrailingSlash(item.url.toLowerCase()));
};

const locationMatchUrl = (path: string): ((item: {url: string}) => boolean) => {
    return (item: { url: string }): boolean => pathMatchUrl(path, item);
};

export const getSelectedMenuItemsByLocation = (
    location: string,
    menuItems: MenuItem[]
): {
    levelOne: MenuItem;
    levelTwo: MenuItem;
    workItems: MenuItem[];
    workItem: MenuItem;
} => {
    let levelOne = menuItems.find(menuLink =>
        menuLink.children.some(subItem =>
            subItem.children.some(locationMatchUrl(location))
        )
    );

    const levelTwo =
        levelOne &&
        levelOne.children.find(subItem =>
            subItem.children.some(locationMatchUrl(location))
        );

    // If level one isn't found, we need to search for a work item
    // instead of a work sub type
    if (!levelOne) {
        levelOne = menuItems.find(menuLink =>
            menuLink.workItems && menuLink.workItems.some(locationMatchUrl(location))
        );
    }

    const workItems = levelTwo ? levelTwo.children : (levelOne && levelOne.workItems) || [];
    const workItem = workItems.find(locationMatchUrl(location));
    return {
        levelOne,
        levelTwo,
        workItems,
        workItem
    };
};

export const getAspectNavigationItemsByLocation = (
    data: LayoutData,
    location: string
): MenuItem[] => {
    const currentPage = data.allMdx.nodes.find(
        node => pathMatchUrl(location, { url: node.fields.slug })
    );

    if (!currentPage || !currentPage.tableOfContents || !currentPage.tableOfContents.items) {
        return [];
    }
    return currentPage.tableOfContents.items.map(item => ({
        name: item.title,
        url: item.url,
        children:
            (item.items &&
                item.items.map(child => ({
                    name: child.title,
                    url: child.url,
                    children: []
                }))
            ) || []
    }));
};

export const flattenAspectNavigationItems = (
    aspectNavigationItems: MenuItem[]
): MenuItem[] => {
    if (!aspectNavigationItems) {
        return [];
    }

    const flattenedAspectNavigationItems = [];
    aspectNavigationItems.forEach(item => {
        flattenedAspectNavigationItems.push(
            {
                name: item.name,
                url: item.url
            }
        );

        if (item.children && item.children.length > 0) {
            item.children.map(child => {
                flattenedAspectNavigationItems.push({
                    name: child.name,
                    url: child.url,
                    child: true
                });
            });
        }
    });

    return flattenedAspectNavigationItems;
};
